/**
 * Formats a type_charge value into a human-readable commercial terms label.
 * @param {string} typeCharge - 'commission' | 'net_price'
 * @returns {string}
 */
export const formatCommercialTerms = (typeCharge) => {
  if (typeCharge === "commission") return "Commission";
  return "Net Price";
};

/**
 * Formats a gender value into a human-readable salutation label.
 * @param {string} gender - 'male' | 'female' | 'other'
 * @returns {string}
 */
export const formatGenderLabel = (gender) => {
  const labels = {
    male: "Male",
    female: "Female",
    other: "Mx."
  };
  return labels[gender] || "--";
};

/**
 * Generates a CSV export filename with today's date in DD-MM-YYYY format.
 * @param {string} prefix - e.g. 'resellers'
 * @returns {string} e.g. 'resellers-01-06-2026.csv'
 */
export const formatExportFilename = (prefix) => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();
  return `${prefix}-${day}-${month}-${year}.csv`;
};

/**
 * Formats a currency code into its full name.
 * @param {string} code - ISO 4217 currency code
 * @returns {string}
 */
export const formatCurrencyName = (code) => {
  const names = {
    USD: "US Dollar",
    EUR: "Euro",
    GBP: "British Pound",
    CHF: "Swiss Franc",
    DKK: "Denmark Krone",
    SEK: "Swedish Krona",
    NOK: "Norwegian Krone",
    ISK: "Icelandic Króna",
    AED: "United Arab Emirates Dirham"
  };
  return names[code] || code;
};

/**
 * Formats a payment method type into a human-readable label.
 * @param {string} type - 'paypal' | 'bank_account'
 * @returns {string}
 */
export const formatPaymentMethodType = (type) => {
  return type === "paypal" ? "Paypal" : "Bank Account";
};

/**
 * Formats a bank transfer type into its full label.
 * @param {string} type - 'ach' | 'wire' | 'swift' | 'transfer_eur' | 'transfer_gbp'
 * @returns {string}
 */
export const formatTransferType = (type) => {
  const labels = {
    ach: "ACH",
    wire: "WIRE",
    swift: "SWIFT",
    transfer_eur: "Transfer to EUR",
    transfer_gbp: "Transfer to GBP"
  };
  return labels[type] || type;
};

/**
 * Formats a bank transfer type into its abbreviated label.
 * @param {string} type - 'ach' | 'wire' | 'swift' | 'transfer_eur' | 'transfer_gbp'
 * @returns {string}
 */
export const formatTransferTypeAbbr = (type) => {
  const labels = {
    ach: "ACH",
    wire: "WIRE",
    swift: "SWIFT",
    transfer_eur: "EUR",
    transfer_gbp: "GBP"
  };
  return labels[type] || type;
};

/**
 * Formats a bank account type into a human-readable label.
 * @param {string} type - 'savings' | 'checking'
 * @returns {string}
 */
export const formatAccountType = (type) => {
  const labels = { savings: "Savings", checking: "Checking" };
  return labels[type] || type;
};

/**
 * Formats address fields into a single comma-separated string.
 * @param {string} address
 * @param {string} city
 * @param {string} postCode
 * @param {string} country
 * @returns {string}
 */
export const formatAddress = (address, city, postCode, country) => {
  return [address, city, postCode, country].filter(Boolean).join(", ");
};

/**
 * Formats an ISO date string into a human-readable date.
 * @param {string|Date} value - ISO date string or Date object.
 * @returns {string} e.g. "01 Jun. 2026 - 10:30 AM"
 */
export const formatDate = (value) => {
  if (!value) return "--";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};
